import { defineComponent as _defineComponent } from 'vue'
import {
  useContext,
  useRoute,
  computed,
  watch,
  onMounted,
  ref,
  useRouter,
} from '@nuxtjs/composition-api';
import isEqual from 'lodash.isequal';
import { v4 } from 'uuid';
import TAccordion from '../atoms/t-accordion.vue';
import SearchFilterLabel from './SearchFilterLabel.vue';
import FilterPills from '~/components/search/FilterPills.vue';
import Card from '~/components/card/Card.vue';
import Pagination from '~/components/Pagination.vue';
import FilterParent from '~/components/filters/FilterParent.vue';
import {
  useSearch,
  mapQueryToFilters,
  getFacetDetails,
  isFacetKey,
  FacetKey,
  formatActiveFilters,
} from '~/composables/search/useSearch';
import { isSortKey, SortValues, Sort } from '~/types/sort';
import SortButtons from '~/components/search/SortButtons.vue';
import { convertToTitleCase, pluralise } from '~/utils/formatStrings';
import TButton from '~/components/atoms/t-button.vue';
import TDropdown from '~/components/atoms/t-dropdown.vue';
import TBadge from '~/components/atoms/t-badge.vue';
import { getFirstItemFromQueryParam } from '~/utils/route';


export default /*#__PURE__*/_defineComponent({
  __name: 'KeywordSearchPage',
  setup(__props) {

const { i18n, $mzAnalyticsApi } = useContext();

const $route = useRoute();
const $router = useRouter();

const searchResultsParentEl = ref<HTMLElement | null>(null);
// this is the query param that was used on the old search page. We want to support
// both mechanisms for now instead of redirecting
const keywordQuery = computed(() =>
  Array.isArray($route.value.query?.query) ? $route.value.query.query[0] : $route.value.query?.query
);
const keyword = computed({
  get() {
    if ($route.value.params?.keyword) {
      return $route.value.params?.keyword;
    }
    // get value for query= if keyword doesn't exist
    return keywordQuery.value || '';
  },
  set() {
    $router.push({
      path: '/search',
      params: { keyword: keyword.value },
      query: { ...$route.value.query, query: undefined },
    });
  },
});

const titleForSearchResults = computed(() => convertToTitleCase(keyword.value) || 'All Resources');

// return the page number from the query param, or 0 if it's not a valid number
const pageOnRouter = computed(() => {
  if (!$route.value.query?.page) {
    return 0;
  }

  const page = Array.isArray($route.value.query?.page)
    ? $route.value.query?.page[0]
    : $route.value.query?.page;
  if (page) {
    const pageNumber = Number.parseInt(page);
    return Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber - 1 : 0;
  }
  return 0;
});
const filtersOnRouter = computed(() => mapQueryToFilters($route.value.query));
const sortOnRouter = computed(() => {
  const sortBy = getFirstItemFromQueryParam($route.value.query?.sort);
  return isSortKey(sortBy) ? sortBy : undefined;
});

const sortValues = SortValues.map(sort => ({
  value: sort,
  label: convertToTitleCase(sort),
}));

const {
  results,
  page,
  sort,
  filters,
  showNoResultsPage,
  facets,
  addFilter,
  removeFilter,
  sendAlgoliaClickEvent,
  clearFilters,
  isSearching,
  formatFacets,
} = useSearch(keyword.value || '', {
  page: pageOnRouter.value,
  filters: filtersOnRouter.value,
  sort: sortOnRouter.value,
});

const facetExpanded = ref<{ [key: string]: boolean }>({
  'year-level-au.lvl0': true,
  'year-level-us.lvl0': true,
  'year-level-gb.lvl0': true,
  postTypeTitle: true,
  'resource-type-au.lvl0': !!filters.value['resource-type-au.lvl0']?.length,
  'resource-type-us.lvl0': !!filters.value['resource-type-us.lvl0']?.length,
  'resource-type-gb.lvl0': !!filters.value['resource-type-gb.lvl0']?.length,
  'learning-area-au.lvl0': !!filters.value['learning-area-au.lvl0']?.length,
  'learning-area-us.lvl0': !!filters.value['learning-area-us.lvl0']?.length,
  'learning-area-gb.lvl0': !!filters.value['learning-area-gb.lvl0']?.length,
  'resource-availability.lvl0': !!filters.value['resource-availability.lvl0']?.length,
  'file-format.lvl0': !!filters.value['file-format.lvl0']?.length,
  'filter-attribute': !!filters.value['filter-attribute']?.length,
  'filter-resource-type.lvl0': true,
  'filter-learning-area.lvl0': !!filters.value['filter-learning-area.lvl0']?.length,
  'filter-price': !!filters.value['filter-price']?.length,
  'filter-format': !!filters.value['filter-format']?.length,
});

const isFilterOn = (key: string, value: string): boolean => {
  const filter = filters.value[key as FacetKey];
  return !!(filter && filter.includes(value));
};

const filterClick = (key: string, value: string, event: Event | boolean) => {
  if (filters.value['filter-resource-type.lvl1']) {
    filters.value['filter-resource-type.lvl1'].forEach(item => {
      if (item.split(' > ')[0] === value) {
        removeFilter('filter-resource-type.lvl1', item);
      }
    });
  }

  if (filters.value['filter-learning-area.lvl1']) {
    filters.value['filter-learning-area.lvl1'].forEach(item => {
      if (item.split(' > ')[0] === value) {
        removeFilter('filter-learning-area.lvl1', item);
      }
    });
  }
  const checked = typeof event === 'boolean' ? event : (event.target as HTMLInputElement).checked;
  if (isFacetKey(key)) {
    trackFilterClick(getFacetDetails(key).label, value, checked);
    if (checked) {
      addFilter(key, value);
    } else {
      removeFilter(key, value);
    }
  } else {
    console.warn('filterClick: unknown key', key);
  }
};

const numFiltersApplied = computed(() => {
  return Object.values(filters.value).reduce((acc, val) => acc + val.length, 0);
});

onMounted(() => {
  $mzAnalyticsApi({
    event: 'pageView',
  });

  if (results.value?.nbHits === 0) {
    trackNoResults();
  }
});

const sortClicked = (sortKey: string) => {
  if (isSortKey(sortKey)) {
    sort.value = sortKey;
    trackSortClick(sortKey);
  } else {
    console.warn('sortClicked: unknown sortKey', sortKey);
  }
};

const areAnyChildFiltersOn = (key: string, value: string): boolean => {
  if (key.includes('.lvl0')) {
    const parentKey = key.replace('.lvl0', '.lvl1');
    return (
      filters.value[parentKey]?.length > 0 &&
      filters.value[parentKey].some(item => item.split(' > ')[0] === value)
    );
  }
  return false;
};

const addToActiveFilters = (
  filters: Record<string, string[]>,
  filterText: string,
  label: string
) => {
  return { ...filters, [filterText]: [...(filters[filterText] || []), label] };
};

const removeFromActiveFilters = (
  filters: Record<string, string[]>,
  filterText: string,
  label: string
) => {
  if (!filters[filterText]) {
    return filters;
  }
  const filtered = filters[filterText].filter(filter => filter !== label);
  return { ...filters, [filterText]: filtered.length ? filtered : undefined };
};

const trackFilterClick = (filterText: string, label: string, itemIsRefined: boolean) => {
  const filterDetails = `${filterText} | ${label}`;
  const activeFilters = formatActiveFilters(filters.value);
  const eventParams = {
    Search_Type_Name: 'Filtered',
    Search_Input: keyword.value,
    Page_Url: $route.value.path,
    Page_Country: i18n.locale,
    Filters_Added: itemIsRefined ? filterDetails : undefined,
    Filters_Removed: !itemIsRefined ? filterDetails : undefined,
    Active_Filters: itemIsRefined
      ? addToActiveFilters(activeFilters, filterText, label)
      : removeFromActiveFilters(activeFilters, filterText, label),
  };
};

const trackSortClick = (sortKey: Sort) => {
  const eventParams = {
    Search_Type_Name: 'Sorted',
    Search_Input: keyword.value,
    Page_Url: $route.value.path,
    Page_Country: i18n.locale,
    Sort_By: sortKey,
    Active_Filters:
      Object.keys(filters.value).length > 0 ? formatActiveFilters(filters.value) : undefined,
  };
};

const trackNoResults = () => {
  const eventParams = {
    Search_Type_Name: 'No Results',
    Search_Input: keyword.value,
    Page_Url: $route.value.path,
    Page_Country: i18n.locale,
    Active_Filters:
      Object.keys(filters.value).length > 0 ? formatActiveFilters(filters.value) : undefined,
  };
};

const scrollToTop = () => {
  if (searchResultsParentEl.value) {
    const headerAndBannersHeight = -300;
    const yOffset =
      searchResultsParentEl.value.getBoundingClientRect().top +
      window.pageYOffset +
      headerAndBannersHeight;
    window.scrollTo({ top: yOffset, behavior: 'smooth' });
  }
};

watch(results, newResults => {
  if (newResults?.nbHits === 0) {
    trackNoResults();
  }
});

watch(
  pageOnRouter,
  (newPage, oldPage) => {
    if (newPage !== oldPage) {
      page.value = newPage;
    }
  },
  { immediate: true }
);

watch(
  filtersOnRouter,
  (newFilters, oldFilters) => {
    if (!isEqual(newFilters, oldFilters) && !isEqual(newFilters, filters.value)) {
      filters.value = newFilters;
    }
  },
  { immediate: true }
);

const numberOfPages = computed(() => results.value?.nbPages ?? 0);
const noResults = computed(() => !results.value?.nbHits || results.value?.nbHits === 0);
// const showNoResultsPage = computed(() => results.value?.showNoResultsPage);

return { __sfc: true,i18n, $mzAnalyticsApi, $route, $router, searchResultsParentEl, keywordQuery, keyword, titleForSearchResults, pageOnRouter, filtersOnRouter, sortOnRouter, sortValues, results, page, sort, filters, showNoResultsPage, facets, addFilter, removeFilter, sendAlgoliaClickEvent, clearFilters, isSearching, formatFacets, facetExpanded, isFilterOn, filterClick, numFiltersApplied, sortClicked, areAnyChildFiltersOn, addToActiveFilters, removeFromActiveFilters, trackFilterClick, trackSortClick, trackNoResults, scrollToTop, numberOfPages, noResults, v4, TAccordion, SearchFilterLabel, FilterPills, Card, Pagination, FilterParent, getFacetDetails, SortButtons, convertToTitleCase, pluralise, TButton, TDropdown, TBadge }
}

})